/**
 * Steam account / sync — INVENTORY-DESKTOP §4 row 6 (lane D4). Prototype lines
 * 1028-1076. Mounted by `DesktopApp` while `s.ptSyncOpen`.
 *
 * `selectors.3e#ptSyncVM` + `PT_SYNC_STEP2` unchanged;
 * `selectors.d4#ptSyncVMDesktop` wraps it for the longer desktop footer (§8).
 *
 * DESKTOP LAYOUT (§4): 620px, and each numbered step is ONE row — the big
 * numeral, a `flex:1` text column, and the step's control on the right (mobile
 * stacked the control under the copy). The footer is likewise one row: the
 * freshness sentence beside `CHECK NOW`.
 *
 * WHAT IS NOT THEATRE (INVENTORY §12, unchanged from the mobile sheet)
 *   1. `SIGN IN ↗` calls `steamLinkStart()` — the real OpenID hand-off in the
 *      Supabase repo, an immediate local link in dev. "WE NEVER GET YOUR
 *      PASSWORD" is a product promise, not a UI string.
 *   2. `PRIVATE — GOT-IN DETECTION OFF` is reachable: `steamLink.profilePublic
 *      === false`, read from the sync result.
 *   3. `CHECK NOW` performs a real round trip and the footer's `CHECKED <N> AGO`
 *      is the freshness slice's `lastOkAt`, so a failure leaves the timestamp
 *      where it was and toasts the retry.
 *
 * DEVIATION — the prototype's unlink line reads `7656…9041 · UNLINK`, with that
 * SteamID64 hard-coded in the template. `SteamLink` carries `personaName` but no
 * id, so the port renders `UNLINK` alone rather than fabricating an account
 * number (INVENTORY §12's rule: never assert a fact the store does not hold).
 *
 * `style-hover` → CSS (§3.11): `d-hov-acc2` on that UNLINK line.
 */

import { Avatar } from '../../components/Avatar.tsx';
import { useQd } from '../../store/index.ts';
import {
  PT_SYNC_ATTR_LINK,
  PT_SYNC_ATTR_TAIL,
  PT_SYNC_PRIVACY,
  PT_SYNC_STEAM_HREF,
  PT_SYNC_STEP2,
} from '../../store/selectors.3e.ts';
import { ptSyncVMDesktop, PT_SYNC_NOTE_DESKTOP } from '../../store/selectors.d4.ts';
import { Modal } from '../components/Modal.tsx';
import type { CSSProperties } from 'react';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

/** Header notes are mono 10.5px `--gt-muted` in the prototype (§4). */
const NOTE: CSSProperties = { fontSize: '10.5px', color: 'var(--gt-muted)' };

const STEP: CSSProperties = {
  background: 'var(--gt-panel)',
  padding: '13px 14px',
  display: 'flex',
  alignItems: 'center',
  gap: 13,
  flexWrap: 'wrap',
};

const NUM: CSSProperties = { fontFamily: PIXEL, fontSize: 9, width: 22, flex: 'none' };

const BODY: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 };

const SUB: CSSProperties = { fontFamily: MONO, fontSize: '9.5px', lineHeight: 1.65, color: 'var(--gt-text-5)' };

/** The right-hand control on step 2 and the footer share this outline box. */
const OUTLINE: CSSProperties = {
  border: '1px solid var(--gt-border-3)',
  color: 'var(--gt-text-3)',
  fontFamily: MONO,
  fontSize: 10,
  padding: '8px 11px',
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  flex: 'none',
};

export default function PtSyncModal(): JSX.Element {
  const s = useQd();
  const v = ptSyncVMDesktop(s, Date.now());

  return (
    <Modal
      label="Steam sync modal"
      width={620}
      onClose={s.closePtSync}
      header={{ title: 'STEAM ACCOUNT', titleClr: 'var(--gt-text)', note: <span style={NOTE}>{PT_SYNC_NOTE_DESKTOP}</span> }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 18px 18px' }}>
        {/* step 1 */}
        <div style={{ ...STEP, border: '1px solid ' + v.s1Bd }}>
          <div style={{ ...NUM, color: v.s1Num }}>1</div>
          <div style={BODY}>
            <div style={{ fontFamily: PIXEL, fontSize: '7.5px', color: 'var(--gt-text)' }}>SIGN IN THROUGH STEAM</div>
            <div style={SUB}>{v.s1Sub}</div>
          </div>
          {v.linked ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
              <Avatar style={{ flex: 'none' }} size={22} fs={7} ini={v.acctIni} bg={v.acctBg} hasAv={false} avBg="" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontFamily: MONO, fontSize: 10, color: 'var(--gt-text)' }}>{v.acctName}</span>
                <span
                  onClick={() => void s.steamUnlink()}
                  className="d-hov-acc2"
                  style={{ fontFamily: MONO, fontSize: 8, color: 'var(--gt-muted-2)', cursor: 'pointer' }}
                >
                  UNLINK
                </span>
              </div>
            </div>
          ) : (
            <div
              onClick={() => {
                if (s.steamBusy) return;
                void s.steamLinkStart();
              }}
              style={{
                background: s.steamBusy ? 'var(--gt-border-2)' : 'var(--acc)',
                color: s.steamBusy ? 'var(--gt-muted)' : 'var(--gt-ink)',
                fontFamily: MONO,
                fontSize: 10,
                fontWeight: 600,
                padding: '9px 12px',
                cursor: s.steamBusy ? 'progress' : 'pointer',
                whiteSpace: 'nowrap',
                flex: 'none',
              }}
            >
              {s.steamBusy ? 'OPENING…' : 'SIGN IN ↗'}
            </div>
          )}
        </div>

        {/* step 2 */}
        <div style={{ ...STEP, border: '1px solid ' + v.s2Bd }}>
          <div style={{ ...NUM, color: v.s2Num }}>2</div>
          <div style={BODY}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: PIXEL, fontSize: '7.5px', color: 'var(--gt-text)' }}>MAKE GAME DETAILS PUBLIC</span>
              <span
                style={{
                  fontFamily: MONO,
                  fontSize: 8,
                  letterSpacing: '.05em',
                  border: '1px solid ' + v.s2ChipBd,
                  color: v.s2ChipClr,
                  background: v.s2ChipBg,
                  padding: '2px 6px',
                }}
              >
                {v.s2Chip}
              </span>
            </div>
            <div style={SUB}>{PT_SYNC_STEP2}</div>
          </div>
          <a href="https://steamcommunity.com/my/edit/settings" target="_blank" rel="noreferrer" style={OUTLINE}>
            PRIVACY SETTINGS ↗
          </a>
        </div>

        {/* attribution + what we store — the app has no other legal surface */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 7,
            fontFamily: MONO,
            fontSize: 9,
            lineHeight: 1.7,
            color: 'var(--gt-muted-2)',
          }}
        >
          <div>
            <a
              href={PT_SYNC_STEAM_HREF}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--gt-text-3)', textDecoration: 'none' }}
            >
              {PT_SYNC_ATTR_LINK}
            </a>
            {PT_SYNC_ATTR_TAIL}
          </div>
          <div>{PT_SYNC_PRIVACY}</div>
        </div>

        {/* freshness footer + CHECK NOW */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            borderTop: '1px dashed var(--gt-border)',
            paddingTop: 12,
            fontFamily: MONO,
            fontSize: 9,
            lineHeight: 1.7,
            color: 'var(--gt-muted-2)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: 240 }}>{v.foot}</div>
          <div
            onClick={() => {
              if (!v.busy) void s.steamSyncNow();
            }}
            style={{ ...OUTLINE, cursor: v.busy ? 'default' : 'pointer' }}
          >
            {v.syncLbl}
          </div>
        </div>
      </div>
    </Modal>
  );
}
